import React, { useMemo } from "react";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Fade from "@mui/material/Fade";
import Paper from "@mui/material/Paper";
import matchingSvg from "../../../assets/matching.svg";
import fileInfo from "../../../assets/fileInfo.svg";
import CheckCircleIcon from "@mui/icons-material/CheckCircleOutline";
import HelpIcon from "@mui/icons-material/HelpOutline";
import ErrorIcon from "@mui/icons-material/ErrorOutline";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import Divider from "@mui/material/Divider";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import { CompanyMatchUploadResponse } from "../../../types/companyMatch";
import { format } from "date-fns/format";
import { TEXT } from "./constants";
import { FileUploadFilterMode } from "./types";

interface SuccessStateProps {
  uploadResult: CompanyMatchUploadResponse;
  mode: FileUploadFilterMode;
}

interface MatchStatProps {
  icon: React.ReactNode;
  label: string;
  count: number;
  color: string;
}

interface InfoStatProps {
  label: string;
  value: string;
}

const MatchStat: React.FC<MatchStatProps> = ({ icon, label, count, color }) => (
  <>
    <ListItem>
      <ListItemIcon sx={{ minWidth: 40 }}>
        <Stack sx={{ color }}>{icon}</Stack>
      </ListItemIcon>
      <ListItemText
        primary={label}
        secondary={count.toString()}
        slotProps={{
          primary: { variant: "subtitle2" },
          secondary: { variant: "subtitle2", align: "right" },
        }}
        sx={{
          display: "flex",
          justifyContent: "space-between",
          m: 0,
        }}
      />
    </ListItem>
  </>
);

const InfoStat: React.FC<InfoStatProps> = ({ label, value }) => (
  <ListItem alignItems="center">
    <ListItemIcon />
    <ListItemText
      primary={label}
      secondary={value}
      slotProps={{
        primary: { variant: "subtitle2" },
        secondary: { variant: "subtitle2", align: "right" },
      }}
      sx={{
        display: "flex",
        justifyContent: "space-between",
        m: 0,
      }}
    />
  </ListItem>
);

export const SuccessState: React.FC<SuccessStateProps> = ({
  uploadResult,
  mode,
}) => {
  const totalRows = uploadResult.totalRows || 0;
  const matchedRows = uploadResult.matchedRows || 0;
  const noMatchedRows = totalRows - matchedRows || 0;
  const invalidRows = uploadResult.invalidRows || 0;
  const salesWins = uploadResult.salesWins || 0;
  const salesLosses = uploadResult.salesLosses || 0;
  const uploadDateTime = useMemo(() => {
    return uploadResult.uploaded
      ? format(new Date(uploadResult.uploaded), "MM/dd/yyyy hh:mm a")
      : "-";
  }, [uploadResult.uploaded]);
  const lastMatchedDate = useMemo(() => {
    return uploadResult.lastMatched
      ? format(new Date(uploadResult.lastMatched), "MM/dd/yyyy hh:mm a")
      : "-";
  }, [uploadResult.lastMatched]);

  return (
    <Fade in={true} timeout={800}>
      <Box>
        <Paper
          elevation={0}
          sx={{
            mb: 1,
            overflow: "hidden",
            borderRadius: 2,
            borderColor: "divider",
            borderWidth: 1,
            borderStyle: "solid",
          }}
        >
          <Stack direction="row" spacing={2} padding={2} alignItems="center">
            <img
              src={matchingSvg}
              alt={TEXT.SUCCESS_STATE.ALT_MATCH_STATS_IMAGE}
              style={{ width: 80, height: 80 }}
            />

            <List dense disablePadding sx={{ width: "100%", flex: 1 }}>
              <MatchStat
                icon={<CheckCircleIcon fontSize="small" />}
                label={TEXT.SUCCESS_STATE.LABEL_MATCHES}
                count={matchedRows}
                color="success.main"
              />
              <Divider component="li" sx={{ marginLeft: 7 }} />
              <MatchStat
                icon={<HelpIcon fontSize="small" />}
                label={TEXT.SUCCESS_STATE.LABEL_NO_MATCH_FOUND}
                count={noMatchedRows}
                color="warning.main"
              />
              <Divider component="li" sx={{ marginLeft: 7 }} />
              <MatchStat
                icon={<ErrorIcon fontSize="small" />}
                label={TEXT.SUCCESS_STATE.LABEL_INVALID_INPUTS}
                count={invalidRows}
                color="error.main"
              />
              {mode === FileUploadFilterMode.ICP_CUSTOMER_LIST &&
                (salesWins > 0 || salesLosses > 0) && (
                  <>
                    <Divider component="li" sx={{ marginLeft: 7 }} />
                    <MatchStat
                      icon={<TrendingUpIcon fontSize="small" />}
                      label={TEXT.SUCCESS_STATE.LABEL_SALES_WON}
                      count={salesWins}
                      color="success.main"
                    />
                    <Divider component="li" sx={{ marginLeft: 7 }} />
                    <MatchStat
                      icon={<TrendingDownIcon fontSize="small" />}
                      label={TEXT.SUCCESS_STATE.LABEL_SALES_LOST}
                      count={salesLosses}
                      color="error.main"
                    />
                  </>
                )}
            </List>
          </Stack>
        </Paper>
        <Paper
          elevation={0}
          sx={{
            mb: 1,
            overflow: "hidden",
            borderRadius: 2,
            borderColor: "divider",
            borderWidth: 1,
            borderStyle: "solid",
          }}
        >
          <Stack direction="row" padding={2} alignItems="center">
            <img
              src={fileInfo}
              alt={TEXT.SUCCESS_STATE.ALT_FILE_INFO_IMAGE}
              style={{ width: 80, height: 80 }}
            />

            <List dense disablePadding sx={{ width: "100%", flex: 1 }}>
              <InfoStat
                label={TEXT.SUCCESS_STATE.LABEL_FILE_NAME}
                value={uploadResult.fileName || "-"}
              />
              <Divider variant="inset" component="li" />
              <InfoStat
                label={TEXT.SUCCESS_STATE.LABEL_UPLOADED}
                value={uploadDateTime}
              />
              <Divider variant="inset" component="li" />
              <InfoStat
                label={TEXT.SUCCESS_STATE.LABEL_LAST_MATCHED}
                value={lastMatchedDate}
              />
            </List>
          </Stack>
        </Paper>
      </Box>
    </Fade>
  );
};
